<template>
  <b-autocomplete
      size="small"
      :data="locationResults"
      v-model="locationSearchText"
      field="description"
      placeholder="Search Address"
      :dropdown-position="config.dropdownPosition"
      :loading="busyFetchingCombinedLocationResults || busyGeocodingPlaceId"
      :disabled="isDisabled"
      @typing="getCombinedResults"
      @select="onSelectLocationResult"
      ref="autocomplete"
    >
      <template slot="empty">No results for {{ locationSearchText }}</template>
      <template slot="footer">
        <div class="mt-1 has-text-right">
          <img src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png" alt="Powered By Google">
        </div>
      </template>
  </b-autocomplete>
</template>

<script>
import { AssetService } from "@/services/asset.service";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";

export default {
  props: {
    withSiteAssets: {
      type: Boolean,
      default: true,
    },
    // Autocomplete props
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    locationResults: [],
    locationSearchText: '',
    googleSessionToken: null,
    busyGeocodingPlaceId: false,
    busyFetchingCombinedLocationResults: false,
  }),
  computed: {
    isDisabled() {
      return this.busyGeocodingPlaceId || this.busyFetchingCombinedLocationResults;
    },
  },
  created() {
    if ( ! _.get(window, 'google.maps.places')) {
      throw new Error('Missing required Google Maps SDK');
    }
  },
  mounted() {
    if (this.config.autofocus) {
      this.$refs.autocomplete.focus();
    }
  },
  beforeDestroy() {
    //
  },
  watch: {
    //
  },
  methods: {
    onSelectLocationResult(location) {
      const vm = this;
      // Regenerate google session token
      this.googleSessionToken = new google.maps.places.AutocompleteSessionToken();
      if (location && location.google_place_id) {
        this.busyGeocodingPlaceId = true;
        const geocoder = new google.maps.Geocoder();
        geocoder
          .geocode({ placeId: location.google_place_id })
          .then(({ results }) => {
            if (results[0]) {
              const payload = {
                latitude: results[0].geometry.location.lat(),
                longitude: results[0].geometry.location.lng(),
              };
              this.$emit('location', payload);
            }
          })
          .catch(err => {
            vm.$buefy.toast.open({
                message: "Failed to get coordinates for specified location.",
                type: 'is-danger'
            });
          })
          .finally(() => {
            this.busyGeocodingPlaceId = false;
          });
      }
      else if (location) {
        this.$emit("location", {
          latitude: location.latitude,
          longitude: location.longitude,
        });
      }
    },
    getCombinedResults: _.debounce(function () {
      const vm = this;
      const search = this.locationSearchText.trim();
      if ( ! search || search.length <= 2) {
        this.locationResults = [];
        return;
      }
      const service = new google.maps.places.AutocompleteService();
      this.googleSessionToken = new google.maps.places.AutocompleteSessionToken();
      const getSiteData = (search) => {
        if (!this.withSiteAssets) return [];
        return new Promise((resolve, reject) => {
          AssetService.all({ asset_name: search, code_code: "SITE" })
            .then((response) => {
              let results = [];
              const assets = JsonDenormalizer.denormalize(response);
              assets.forEach(asset => {
                const latitude = _.get(asset, 'assetNodes.0.latitude');
                const longitude = _.get(asset, 'assetNodes.0.longitude');
                if (latitude && longitude) {
                  results.push({
                    description: asset.assetName,
                    latitude: latitude,
                    longitude: longitude,
                  });
                }
              });
              resolve(results);
            })
            .catch(err => reject(err));
        });
      };
      const getAddressData = function(search) {
        return new Promise((resolve, reject) => {
          let results = [];
          service.getPlacePredictions(
            {
              input: search,
              types: ['geocode'], // 'address' requires exact street address
              sessionToken: vm.googleSessionToken,
              componentRestrictions: { country: 'nz' }
            },
            function(predictions, status) {
              if ( ! [window.google.maps.places.PlacesServiceStatus.OK, window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS].includes(status)) {
                window.console && console.log(status);
                reject(new Error(`Unexpected status from Google Maps SDK.`));
              }
              else {
                (predictions || []).forEach(item => {
                  results.push({
                    latitude: null, // need to geocode if user picks it
                    longitude: null,
                    description: item.description,
                    google_place_id: item.place_id,
                  });
                });
                resolve(results);
              }
            }
          );
        });
      };
      vm.busyFetchingCombinedLocationResults = true;
      Promise.all([
        getSiteData(search),
        getAddressData(search),
      ])
      .then(all => {
        const site_results = all[0];
        const address_results = all[1];
        this.locationResults = address_results.concat(site_results);
      })
      .catch(err => {
        vm.$buefy.toast.open({
            message: err.message,
            type: 'is-danger'
        });
      })
      .finally(() => {
        vm.busyFetchingCombinedLocationResults = false;
      })
    }, 500, { leading: false, trailing: true }),
  },
};
</script>

<style lang="scss">

</style>


// WEBPACK FOOTER //
// src/components/Order/LocationAutocomplete.vue